'use client';

import { Box, Button, Typography } from '@/primitives';
import { blue, slate } from '@/lib/colours';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useRouter } from 'next/navigation';
import { PUBLIC_ROUTES } from '@/configs/routes';
import Image from 'next/image';

interface ErrorBoundaryFallbackProps {
  error?: Error;
  resetError?: () => void;
}

export function ErrorBoundaryFallback({
  error,
  resetError,
}: ErrorBoundaryFallbackProps) {
  const router = useRouter();

  const handleRefresh = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    router.push(PUBLIC_ROUTES.HOME);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: '800px',
          padding: '32px',
          borderRadius: '12px',
          backgroundColor: slate[50],
          border: `1px solid ${slate[200]}`,
        }}
      >
        {/* Friendly cow image */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Image
            src="/images/1575ab8e-d7a5-4762-8a94-12fd4604e512.png"
            alt="Friendly cow illustration"
            width={200}
            height={150}
            priority
            style={{
              width: '100%',
              maxWidth: '200px',
              height: 'auto',
            }}
          />
        </Box>

        <Typography
          variant="headlinelarge"
          sx={{
            color: slate[900],
            marginBottom: '16px',
          }}
        >
          Oops! Something went wrong
        </Typography>

        <Typography
          variant="bodymedium"
          sx={{
            color: slate[600],
            marginBottom: '24px',
            lineHeight: 1.6,
          }}
        >
          We encountered an unexpected error. Don&apos;t worry - we&apos;ve
          logged this issue and one of our developers will take a look.
        </Typography>

        {process.env.NODE_ENV === 'development' && error && (
          <Box
            sx={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: slate[100],
              borderRadius: '8px',
              border: `1px solid ${slate[300]}`,
            }}
          >
            <Typography
              variant="labelsmall"
              sx={{
                color: slate[700],
                fontFamily: 'monospace',
                wordBreak: 'break-word',
                fontSize: '12px',
              }}
            >
              {error.message}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{
              backgroundColor: blue[600],
              '&:hover': {
                backgroundColor: blue[700],
              },
            }}
          >
            Try Again
          </Button>

          <Button
            variant="outlined"
            onClick={handleGoHome}
            sx={{
              borderColor: slate[300],
              color: slate[700],
              '&:hover': {
                borderColor: slate[400],
                backgroundColor: slate[50],
              },
            }}
          >
            Go to Home
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
